/**
 * =========================================================================================
 * Pricing Plan Page
 * =========================================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { showToastError, showToastSuccess } from 'ubs-framework/v2/common/function';
import useSocket from '../../../../common/useSocket';
import { sendData } from '../../../../../store/actions';
import UCustomScrollBar from 'ubs-framework/v2/UCustomScrollBar';
import UButton from 'ubs-framework/v2/UButton';
import { BILLING_CYCLE, INDIA_COUNTRY_IDS, SUBSCRIPTION_STATUS } from '../../../../../common/constant';
import useUAlert from 'ubs-framework/v2/UAlert';
import { getCashfreeInstance } from '../../common/cashfree-loader';
import { dateFormatter } from 'ubs-framework/v2/common/function';
import PlanGrid from './components/plan-grid';
import PlanComparison from './components/plan-comparison';
import OrderSummary from './components/order-summary';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UAlertMessage from 'ubs-framework/v2/UAlertMessage';

const PricingPlan = () => {
    const history = useHistory();
    const alert = useUAlert();
    const dispatch = useDispatch();
    const _ORG_ID = useSelector(e => e?.common?.orgId);
    const _PI = useSelector(e => e?.common?.planInfo);
    const _IS_SAMPLE_DATA = useSelector(e => e?.common?.authData?.isSampleData);
    const _AUTH_COUNTRY = useSelector(e => e?.common?.country);
    const _RESUME_PAYMENT = useSelector(e => e.common?.pendingPayment);

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [seatCount, setSeatCount] = useState(1);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [priceCalc, setPriceCalc] = useState(null);
    const [calcLoading, setCalcLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [currency, setCurrency] = useState(() => INDIA_COUNTRY_IDS?.includes(_AUTH_COUNTRY) ? 'INR' : 'USD');
    const [billingCycle, setBillingCycle] = useState(_PI?.billingCycle || BILLING_CYCLE.MONTHLY);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [subscriptionFetch, setSubscriptionFetch] = useState(null);
    const [planFetchCompleted, setPlanFetchCompleted] = useState(false);
    const [totalEmpCount, setTotalEmpCount] = useState(0);
    const [pendingMod, setPendingMod] = useState(null);
    const [cancelModLoading, setCancelModLoading] = useState(false);
    const lastFetchedPlanRef = useRef(null);
    const pendingModifyParamsRef = useRef(null);
    const planListCalledRef = useRef(false);
    const prevCurrencyRef = useRef(currency);

    const selectedPlan = plans.find(p => p._id === selectedPlanId);
    const isCancelled = [SUBSCRIPTION_STATUS.CANCELLED, SUBSCRIPTION_STATUS.CUSTOMER_CANCELLED].includes(subscriptionFetch?.status);

    const { refresh, off } = useSocket(res => {
        if (!res.error) {
            switch (res.event) {
                case 'billing|cashfree|subscription|create':
                    setLoading(false);
                    const sessionId = res?.data?.subscription_session_id;
                    if (sessionId) {
                        history.push(`/${_ORG_ID}/manage/subscriptions/checkout?sessionId=${sessionId}`);
                    }
                    break;
                case 'billing|cashfree|subscription|modify':
                    if (res?.data?.action === 'MODIFICATION_ALREADY_PENDING' && res?.data?.canCancel) {
                        setLoading(false);
                        setPendingMod(res.data);
                        break;
                    }
                    setPendingMod(null);
                    if (res?.data?.action === 'PAYMENT_REQUIRED' && res?.data?.proratedOrder?.paymentLink) {
                        initiateProratedPayment(res.data.proratedOrder);
                    } else {
                        setLoading(false);
                        alert.show({ title: 'Subscription Updated', description: res?.data?.message || 'Your subscription has been modified successfully.' });
                        history.push(`/${_ORG_ID}/manage/subscriptions/payments`);
                    }
                    break;
                case 'billing|cashfree|subscription|cancelModification':
                    setCancelModLoading(false);
                    setPendingMod(null);
                    showToastSuccess(res?.data?.message || 'Scheduled change cancelled successfully.');
                    setPreviewData(null);
                    setPlanFetchCompleted(false);
                    lastFetchedPlanRef.current = null;
                    sendData(dispatch, 'billing|plan|fetch', { planId: _PI?._id });
                    break;
                case 'billing|plan|fetch':
                    const subInfo = res?.data?.subscriptionInfo || null;
                    setSubscriptionInfo(subInfo);
                    const plan = plans?.find(e => e?._id === selectedPlanId);
                    if (subInfo?.billingCycle) setBillingCycle(subInfo.billingCycle);
                    if (subInfo?.seatCount) {
                        const isPlanChanged = subInfo?.planId !== selectedPlanId;
                        if (isPlanChanged || isCancelled) {
                            setSeatCount(Math.max(plan?.minEmployee || 1, totalEmpCount));
                        } else {
                            setSeatCount(Math.max(subInfo.seatCount + 1, totalEmpCount));
                        }
                    }
                    setPlanFetchCompleted(true);
                    break;
                case 'billing|cashfree|subscription|previewModify':
                    setPreviewData(res?.data || null);
                    break;
                case 'billing|cashfree|subscription|calculatePrice':
                    setCalcLoading(false);
                    setPriceCalc(res?.data || null);
                    break;
                case 'billing|cashfree|subscription|fetch':
                    if (res?.data?.status) {
                        setSubscriptionFetch({status: res.data.status, currency: res.data.currency});
                        setBillingCycle(res?.data?.billingCycle)
                    }
                    break;
                case 'account|employee|activeCount':
                    setTotalEmpCount(res?.data?.totalEmployeeCount || 0);
                    setSeatCount(res?.data?.totalEmployeeCount || 1);
                    break;
                case 'billing|cashfree|subscription|resume':
                    setLoading(false);
                    showToastSuccess(res?.data?.message || 'Your subscription has been resumed successfully.');
                    history.push(`/${_ORG_ID}/manage/subscriptions/payments`);
                    break;
                case 'billing|plan|list':
                    let planList = res?.data || [];
                    if (planList?.find(item => item?._id === _PI?.planId)?.type > 1) {
                        planList = planList?.filter(item => item?.type !== 1);
                    }

                    const formattedPlans = planList?.map(item => {
                        const track = item?.track;
                        if (!track) return null;

                        const defaultInfo = track.default || {};
                        const features = [];

                        Object.keys(track)?.forEach(key => {
                            if (key !== 'default') {
                                const feature = track[key];
                                if (feature && feature.value === true) {
                                    features.push({ title: feature.title, limit: item?.type === 1 ? feature.limit : null });
                                }
                            }
                        });

                        return {
                            _id: item._id || item.plan,
                            title: item.plan,
                            plan_code: item.plan_code,
                            subtitle: defaultInfo.note || '',
                            price: defaultInfo.price || 0,
                            billingText: 'month',
                            currency: item?.currency || 'INR',
                            minEmployee: defaultInfo.minEmployee || 1,
                            features: features,
                            isRecommended: false,
                            type: item?.type,
                            isCurrent: item?._id === _PI?.planId,
                            badge: item?._id === _PI?.planId ? 'Current' : '',
                            discount: item?.discount || null,
                        };
                    }).filter(Boolean);

                    setPlans(formattedPlans);
                    setDataLoaded(true);
                    break;
                default:
                    return true;
            }
        } else {
            if (res.event === 'billing|cashfree|subscription|cancelModification') {
                setCancelModLoading(false);
                showToastError(res?.data?.error || 'Failed to cancel modification. Please try again.');
                return;
            }
            switch (res?.code) {
                case 403:
                case 400:
                case 412:
                case 404:
                    setLoading(false);
                    alert.show({ title: 'something went wrong', description: res?.data?.error });
                    break;
                default:
                    return true;
            }
        }
    });

    useEffect(() => {
        sendData(dispatch, 'account|employee|activeCount', {})
        sendData(dispatch, 'billing|cashfree|subscription|resumePayment', {});
        if (_PI?.subscription) {
            sendData(dispatch, 'billing|cashfree|subscription|fetch', {
                subscriptionId: _PI?.subscription, syncWithCashfree: true
            });
        }
    }, []);

    useEffect(() => {
        refresh();

        const subscriptionCurrency = _RESUME_PAYMENT?.data?.subscription?.currency || subscriptionFetch?.currency;
        const resolvedCurrency = (!!_PI?.subscription && subscriptionCurrency) ? subscriptionCurrency : currency;

        if (!!_PI?.subscription && (subscriptionCurrency)) {
            setCurrency(subscriptionCurrency);
        }

        if (prevCurrencyRef.current !== currency) {
            prevCurrencyRef.current = currency;
            planListCalledRef.current = false;
        }

        const canFetch = step === 1 && (!!_PI?.subscription ? subscriptionCurrency : !!resolvedCurrency);

        if (canFetch && !planListCalledRef.current) {
            planListCalledRef.current = true;
            sendData(dispatch, 'billing|plan|list', { currency: resolvedCurrency });
        }

        return () => off();
    }, [_RESUME_PAYMENT?.data, currency, selectedPlanId, subscriptionFetch]);

    useEffect(() => {
        if (step !== 2 || !_PI?.subscription) return;
        if (lastFetchedPlanRef.current === selectedPlanId) return;
        lastFetchedPlanRef.current = selectedPlanId;
        setPlanFetchCompleted(false);
        sendData(dispatch, 'billing|plan|fetch', { planId: _PI?._id });
    }, [step, selectedPlanId, _PI?._id, _PI?.subscription]);

    useEffect(() => {
        if (step !== 2 || !selectedPlanId) return;
        if (!_PI?.subscription && !planFetchCompleted) return;
        if (_PI?.subscription && !planFetchCompleted) return;
        const getSelectedPlan = plans?.find(e => e?._id === selectedPlanId);
        setCalcLoading(true);
        setPreviewData(null);
        setPriceCalc(null);
        if (_PI?.subscription && !isCancelled) {
            sendData(dispatch, 'billing|cashfree|subscription|previewModify', {
                subscriptionId: _PI?.subscription,
                ...selectedPlanId !== _PI?.planId && { newPlanId: selectedPlanId },
                ...seatCount !== subscriptionInfo?.seatCount && { newSeatCount: seatCount },
                ...billingCycle !== subscriptionInfo?.billingCycle && { newBillingCycle: billingCycle },
            })
        }
        sendData(dispatch, 'billing|cashfree|subscription|calculatePrice', {
            planId: selectedPlanId,
            seatCount,
            billingCycle,
            planInfo: {
                plan: getSelectedPlan?.title,
                plan_code: getSelectedPlan?.plan_code,
                discount: getSelectedPlan?.discount,
                currency,
                track: {
                    default: {
                        price: getSelectedPlan?.price,
                        minEmployee: getSelectedPlan?.minEmployee
                    }
                }
            }
        });
    }, [step, selectedPlanId, seatCount, billingCycle, planFetchCompleted, _PI?.subscription, subscriptionInfo, plans]);

    const initiateProratedPayment = async (proratedOrder) => {
        try {
            const cashfreeInstance = await getCashfreeInstance();
            const result = await cashfreeInstance.checkout({
                paymentSessionId: proratedOrder.paymentLink,
                redirectTarget: '_self',
            });

            if (result?.error) {
                alert.show({ title: 'Payment Failed', description: result.error.message || 'Payment could not be completed.' });
            }
        } catch (err) {
            alert.show({ title: 'Payment Error', description: 'Failed to initiate payment. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleContinue = (planId) => {
        if (!!_IS_SAMPLE_DATA) {
            alert.show({
                title: 'Remove all sample data?',
                description: `Firstly, you need to remove sample data to subscribe.`,
                close: 'Cancel',
                icon: <Icon.Delete fill={COLOR.u_red}/>,
                submit: 'Yes, Clear Data',
                onSubmit: () => {
                    dispatch({type: 'SET_SAMPLE_DATA', payload: false});
                    dispatch({type: 'SET_SETUP_DONE', payload: {flag: true, isLoading: true}});
                    dispatch({type: 'SET_PREPARING_DATA', payload: true});
                    sendData(dispatch, 'track|master|sampledata|remove', {isSampleData: false})
                }
            })
            return;
        }

        if (!planId) {
            showToastError('Please select a plan');
            return;
        }
        setStep(2);
    };

    const handleSubscribe = () => {
        if (!selectedPlanId) {
            showToastError('Please select a plan');
            return;
        }
        if (selectedPlan && seatCount < selectedPlan.minEmployee) {
            showToastError(`Minimum ${selectedPlan.minEmployee} users required`);
            return;
        }
        setLoading(true);
        setPendingMod(null);
        if (_PI?.subscription && !isCancelled) {
            const modifyParams = {
                subscriptionId: _PI?.subscription,
                ...selectedPlanId !== _PI?.planId && { newPlanId: selectedPlanId },
                ...seatCount !== subscriptionInfo?.seatCount && { newSeatCount: seatCount },
                ...billingCycle !== subscriptionInfo?.billingCycle && { newBillingCycle: billingCycle },
            };
            pendingModifyParamsRef.current = modifyParams;
            sendData(dispatch, 'billing|cashfree|subscription|modify', modifyParams);
        } else {
            sendData(dispatch, 'billing|cashfree|subscription|create', {
                planId: selectedPlanId,
                seatCount: seatCount,
                billingCycle,
            });
        }
    };

    const handleCancelModification = () => {
        setCancelModLoading(true);
        sendData(dispatch, 'billing|cashfree|subscription|cancelModification', { subscriptionId: _PI?.subscription });
    };

    const handleCancelAndApplyNew = () => {
        if (!pendingModifyParamsRef.current) return;
        setPendingMod(null);
        setLoading(true);
        sendData(dispatch, 'billing|cashfree|subscription|modify', {
            ...pendingModifyParamsRef.current,
            replacePending: true,
        });
    };

    const handleKeepCurrent = () => {
        setPendingMod(null);
    };

    const handleResumeSubscription = () => {
        const subscriptionId = _PI?.subscription;
        if (!subscriptionId) return;
        setLoading(true);
        sendData(dispatch, 'billing|cashfree|subscription|resume', { subscriptionId });
    };

    return (
        <React.Fragment>
            <div className="u_layout-list">
                <div className="u_layout-scrollview">
                    <UCustomScrollBar>
                        <div className="u_pricing-plan">
                            {step === 1 ? (
                                <PlanGrid
                                    plans={plans}
                                    dataLoaded={dataLoaded}
                                    selectedPlanId={selectedPlanId}
                                    onSelectPlan={setSelectedPlanId}
                                    onContinue={handleContinue}
                                    currency={currency}
                                    onCurrencyChange={setCurrency}
                                    subscriptionStatus={subscriptionFetch?.status}
                                    onResumeSubscription={handleResumeSubscription}
                                    billingCycle={billingCycle}
                                    onBillingCycleChange={setBillingCycle}
                                />
                            ) : selectedPlan && (
                                <>

                                <div className="u_pricing-plan__summary u_grid">
                                    {[SUBSCRIPTION_STATUS.CANCELLED, SUBSCRIPTION_STATUS.CUSTOMER_CANCELLED]?.includes(subscriptionFetch?.status) && (!_PI?.end || new Date(_PI?.end) > new Date(new Date().setHours(0, 0, 0, 0))) && <div id="resubscribe-warning-alert" className="u_pricing-plan__alert">
                                        <UAlertMessage danger full>
                                            <div className="u_flex">
                                                <Icon.Warning size={16} fill={COLOR.u_red} className="u_mr-10 u_flex-shrink"/>
                                                Your previous plan is already paid and active until {_PI?.end ? dateFormatter({ date: _PI?.end, format: 'MMMM DD' }) : 'the end date'}, and you may continue using it until that date. If you choose to resubscribe now, the new plan will begin from today and the previous plan will be replaced.
                                            </div>
                                        </UAlertMessage>
                                    </div>}
                                    <PlanComparison
                                        selectedPlan={selectedPlan}
                                        billingCycle={billingCycle}
                                        seatCount={seatCount}
                                        totalEmpCount={totalEmpCount}
                                        previewData={previewData}
                                        setPlanFetchCompleted={setPlanFetchCompleted}
                                        hasSubscription={!!_PI?.subscription && !isCancelled}
                                        subscriptionInfo={subscriptionInfo}
                                        subscriptionStatus={subscriptionFetch?.status}
                                        _PI={_PI}
                                        onGoBack={() => {
                                            setStep(1)
                                            setPlanFetchCompleted(false)
                                        }}
                                        onBillingCycleChange={setBillingCycle}
                                        onSeatCountChange={setSeatCount}
                                        currency={currency}
                                    />
                                    {pendingMod ? (
                                        <div className="u_pricing-plan__invoice u_b-light">
                                            <div className="u_flex-align u_mb-16 u_bb-light u_pb-18">
                                                <Icon.Warning size={18} fill={COLOR?.u_orange} className="u_mr-10" />
                                                <h4 className="u_semi-large-font u_fw-700">Scheduled Change Pending</h4>
                                            </div>
                                            <p className="u_small-font u_color-font u_mb-16">
                                                {pendingMod?.message || 'A plan change is already scheduled. Choose how to proceed.'}
                                            </p>
                                            {pendingMod?.pendingModification && (
                                                <div className="u_bg-thead u_radius-8 u_px-16 u_py-14 u_mb-16 u_b-light">
                                                    <div>

                                                        <div className="u_flex-align u_content-between u_mb-12">
                                                            <span className="u_small-font u_color-font">Seats</span>
                                                            <span className="u_small-font u_fw-600 u_flex-align">
                                                                ({pendingMod?.pendingModification?.before?.seatCount}
                                                                   <Icon.ArrowRightLine size={12} fill="currentColor" className="u_mx-8" />
                                                                {pendingMod?.pendingModification?.after?.seatCount})
                                                            </span>
                                                        </div>

                                                        <div className="u_flex-align u_content-between u_mb-12">
                                                            <span className="u_small-font u_color-font">Plan</span>
                                                            <span className="u_small-font u_fw-600 u_flex-align">
                                                                ({pendingMod?.pendingModification?.before?.planCode}
                                                                   <Icon.ArrowRightLine size={12} fill="currentColor" className="u_mx-8" />
                                                                {pendingMod?.pendingModification?.after?.planCode})
                                                            </span>
                                                        </div>

                                                        <div className="u_flex-align u_content-between u_mb-12">
                                                            <span className="u_small-font u_color-font">Type</span>
                                                            <span className="u_small-font u_fw-600">
                                                                {pendingMod?.pendingModification?.type?.toLowerCase()}
                                                            </span>
                                                        </div>

                                                        <div className="u_flex-align u_content-between u_mb-12">
                                                            <span className="u_small-font u_color-font">Status</span>
                                                            <span className="u_small-font u_fw-600">
                                                                {pendingMod?.pendingModification?.status?.toLowerCase()}
                                                            </span>
                                                        </div>

                                                        {pendingMod?.pendingModification?.effectiveDate && <div className="u_flex-align u_content-between">
                                                            <span className="u_small-font u_color-font">Effective Date</span>
                                                            <span className="u_small-font u_fw-600">
                                                                {dateFormatter({ date: pendingMod?.pendingModification?.effectiveDate, format: 'DD MMM YYYY' })}
                                                            </span>
                                                        </div>}
                                                    </div>
                                                </div>
                                            )}
                                            <p className="u_small-font u_color-font u_mb-20">
                                                You must cancel this scheduled change before applying new ones, or cancel and apply your new change in one step.
                                            </p>
                                            <div className="u_flex-align u_mb-12">
                                                <UButton
                                                    type={loading ? 'Applying...' : 'Cancel & Apply New'}
                                                    variant="blue"
                                                    onClick={handleCancelAndApplyNew}
                                                    disabled={loading || cancelModLoading}
                                                    cn="u_mr-10 u_payment-btn"
                                                    style={{ borderRadius: '6px' }}
                                                />
                                                <UButton
                                                    type={cancelModLoading ? 'Cancelling...' : 'Cancel Change'}
                                                    variant="white"
                                                    onClick={handleCancelModification}
                                                    disabled={loading || cancelModLoading}
                                                    cn="u_payment-btn"
                                                    style={{ borderRadius: '6px' }}
                                                />
                                            </div>
                                            <UButton
                                                type="Keep Current"
                                                variant="white"
                                                onClick={handleKeepCurrent}
                                                disabled={loading || cancelModLoading}
                                                cn="u_payment-btn"
                                                style={{ borderRadius: '6px' }}
                                            />
                                        </div>
                                    ) : (
                                        <OrderSummary
                                            selectedPlan={selectedPlan}
                                            priceCalc={priceCalc}
                                            previewData={previewData}
                                            loading={loading}
                                            calcLoading={calcLoading}
                                            onSubscribe={handleSubscribe}
                                            currency={currency}
                                            isResubscribe={isCancelled && (!_PI?.end || new Date(_PI?.end) > new Date(new Date().setHours(0, 0, 0, 0)))}
                                            billingCycle={billingCycle}
                                        />
                                    )}
                                </div>
                                </>
                            )}
                        </div>
                    </UCustomScrollBar>
                </div>
            </div>
        </React.Fragment>
    );
};

export default PricingPlan;
